import { z } from 'zod'

export const OperationsRunnerSchema = z
  .object({
    runner_id: z.number(),
    runner_name: z.string(),
    site: z.string(),
    os: z.string(),
    status: z.enum(['online', 'offline']).catch('offline'),
    busy: z.boolean(),
    version: z.string().nullable(),
    labels: z.array(z.string()),
    observed_at: z.string(),
    utilization_24h: z.number(),
  })
  .passthrough()
export type OperationsRunner = z.infer<typeof OperationsRunnerSchema>

export const OperationsJobSchema = z
  .object({
    github_job_id: z.number(),
    github_run_id: z.number(),
    repository: z.string(),
    workflow_name: z.string(),
    job_name: z.string(),
    status: z.enum(['queued', 'in_progress', 'completed']).catch('completed'),
    conclusion: z.string().nullable(),
    runner_name: z.string().nullable(),
    routing: z
      .enum(['self-hosted', 'github-hosted', 'unknown'])
      .catch('unknown'),
    expected_self_hosted: z.boolean(),
    cache_state: z.enum(['cold', 'warm', 'unknown']).catch('unknown'),
    queued_at: z.string(),
    started_at: z.string().nullable(),
    completed_at: z.string().nullable(),
    html_url: z.string().nullable(),
  })
  .passthrough()
export type OperationsJob = z.infer<typeof OperationsJobSchema>

export const OperationsBaselineSchema = z
  .object({
    workflow_name: z.string(),
    job_name: z.string(),
    cache_state: z.enum(['cold', 'warm', 'unknown']).catch('unknown'),
    sample_count: z.number(),
    median_seconds: z.number(),
    p95_seconds: z.number(),
  })
  .passthrough()
export type OperationsBaseline = z.infer<typeof OperationsBaselineSchema>

export const OperationsCheckSchema = z
  .object({
    kind: z.enum([
      'zfs_replication',
      'zfs_restore_drill',
      'qnap_restore_drill',
      'gitlab_backup',
      'gitlab_primary',
      'gitlab_secondary',
      'qnap',
    ]),
    source: z.string(),
    status: z.enum(['ok', 'warning', 'error']).catch('error'),
    observed_at: z.string(),
    duration_ms: z.number().nullable(),
    bytes: z.number().nullable(),
    reference: z.string().nullable(),
    message: z.string().nullable(),
    details: z.unknown(),
  })
  .passthrough()
export type OperationsCheck = z.infer<typeof OperationsCheckSchema>

export const PromotionRequestSchema = z
  .object({
    id: z.string().uuid(),
    status: z.enum(['pending', 'claimed', 'succeeded', 'failed']),
    requested_at: z.string(),
    claimed_at: z.string().nullable(),
    completed_at: z.string().nullable(),
    result_message: z.string().nullable(),
  })
  .passthrough()
export type PromotionRequest = z.infer<typeof PromotionRequestSchema>

export const AdminOperationsResponseSchema = z
  .object({
    generated_at: z.string(),
    expected_runners: z.array(z.string()),
    runners: z.array(OperationsRunnerSchema),
    runner_history: z.array(
      z.object({
        runner_name: z.string(),
        bucket: z.string(),
        busy_percent: z.number(),
        online_percent: z.number(),
      }),
    ),
    ci: z.object({
      queued_count: z.number(),
      in_progress_count: z.number(),
      oldest_queued_at: z.string().nullable(),
      unexpected_fallbacks_24h: z.number(),
      queue_wait_24h: z.object({
        average_seconds: z.number().nullable(),
        sample_count: z.number(),
        total_seconds: z.number(),
      }),
      queue_wait_7d: z.object({
        average_seconds: z.number().nullable(),
        sample_count: z.number(),
        total_seconds: z.number(),
      }),
      busy_time_24h: z.object({
        total_seconds: z.number(),
        job_count: z.number(),
      }),
      busy_time_7d: z.object({
        total_seconds: z.number(),
        job_count: z.number(),
      }),
      recent_jobs: z.array(OperationsJobSchema),
      baselines: z.array(OperationsBaselineSchema),
    }),
    infrastructure: z.array(OperationsCheckSchema),
    infrastructure_history: z.array(OperationsCheckSchema),
    promotion: z.object({
      enabled: z.boolean(),
      blocker: z.string(),
      confirmation_phrase: z.string(),
      agent_last_seen_at: z.string().nullable(),
      drill_completed_at: z.string().nullable(),
      qnap_drill_completed_at: z.string().nullable(),
      latest_request: PromotionRequestSchema.nullable(),
    }),
  })
  .passthrough()
export type AdminOperationsResponse = z.infer<
  typeof AdminOperationsResponseSchema
>

export const OperationsAlertSummarySchema = z
  .object({
    generated_at: z.string(),
    level: z.enum(['ok', 'critical']),
    message: z.string().nullable(),
  })
  .passthrough()
export type OperationsAlertSummary = z.infer<
  typeof OperationsAlertSummarySchema
>
